import { Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

import { Multimedia, Link } from '@/features/elements';
import {
	noInputBlocks,
	multimediaBlocks,
	textInputBlocks,
	typingTimeout,
	alwaysNotVisibleBlocks
} from '@/constants';
import { makeClassName, tryFocus } from '@/utils';
import { useTimeout } from '@/hooks';
import { useConfigStore, useBlock, useFormStore } from '@/features/form';

import Wrap from './Wrap';
import Bubble from './Bubble';
import Answer from './Answer';
import BlockInput from './BlockInput';
import Typing from './Typing';
import Text from './Text';
import Icon from './Icon';
import RestartButton from './RestartButton';
import Delay from './Delay';

export default function Message({
	blockId,
	prevBlockId,
	setScrollPosition,
	elementIndex
}) {
	const {
		formType,
		formOpen,
		lastKeyPressed,
		conversationalDummyFocusBlockRef,
		conversationalFocusStateValue
	} = useFormStore();
	const { properties, text } = useConfigStore();
	const { block, state, actions, errorMessage } = useBlock({ id: blockId });
	const prevBlock = useBlock({ id: prevBlockId });
	const [doneTyping, setDoneTyping] = useState(false);
	const [bubbleDidShow, setBubbleDidShow] = useState(false);
	const typingDelay = Number(properties.typingTimeout ?? typingTimeout);
	const restartRef = useRef(null);

	useTimeout(
		() => {
			setDoneTyping(true);
		},
		formOpen || formType === 'embed' ? typingDelay : null
	);

	useEffect(() => {
		setScrollPosition();
	}, [bubbleDidShow, setScrollPosition]);

	const { avatar } = properties;

	const BubbleProps = {
		align: 'left',
		blockType: block.typeName,
		prevBlockType: prevBlock?.block?.typeName,
		avatar,
		id: block.id,
		name: text?.botName,
		block
	};

	const hasSetValue =
		textInputBlocks.indexOf(block.typeName) > -1
			? state.value && state.inputSet
			: state.value || state.screenshotSet;

	const showAnswer =
		(doneTyping && state.isSkipped) ||
		(doneTyping && hasSetValue && !state.showError && !state.isLoading);

	return (
		<div
			className={makeClassName(
				'chat-container',
				!doneTyping && 'is-typing',
				noInputBlocks.indexOf(block.typeName) > -1 && 'non-interactive-element'
			)}
		>
			<Wrap
				key={block.id}
				className={makeClassName(
					'bubble-wrap',
					`bubble-wrap-${block.typeName}`,
					{
						className: `${block.typeName !== 'rating' ? block.typeName : ''}-group`,
						prefix: false
					},
					{ className: `${block.typeName}-group-${state.typeCount}`, prefix: false }
				)}
				show={
					alwaysNotVisibleBlocks.indexOf(block.typeName) > -1 ? false : state.isVisible
				}
				id={`${block.typeName}-${block.id}`}
			>
				{!doneTyping && (
					<Bubble {...BubbleProps}>
						<Typing />
					</Bubble>
				)}
				{doneTyping && (
					<Bubble
						{...BubbleProps}
						outerContent={
							multimediaBlocks.indexOf(block.typeName) > -1 ? (
								<Multimedia block={block} />
							) : null
						}
						onAnimationEnd={() => setBubbleDidShow(true)}
					>
						{block.typeName === 'icon' ? (
							<Icon icon={block?.properties?.icon} />
						) : ['link', 'ending'].indexOf(block.typeName) > -1 ? (
							<Link
								block={block}
								conversational
							/>
						) : block.typeName !== 'multimedia' ||
						  (block.typeName === 'multimedia' && block.title) ? (
							<Text
								block={block}
								text={block.title}
							/>
						) : null}
					</Bubble>
				)}
				{doneTyping && block.typeName === 'ending' && block.properties?.show_restart && (
					<Delay delay={1000}>
						<RestartButton
							ref={restartRef}
							block={block}
							onAnimationEnd={() => {
								setScrollPosition();
								tryFocus(restartRef.current);
							}}
						/>
					</Delay>
				)}

				{showAnswer && (
					<Bubble align="right">
						<Answer
							value={state.value}
							state={state}
							block={block}
						/>
					</Bubble>
				)}
			</Wrap>
			{doneTyping &&
				bubbleDidShow &&
				!showAnswer &&
				noInputBlocks.indexOf(block.typeName) === -1 && (
					<BlockInput
						state={state}
						block={block}
						setValue={actions.setValue}
						setNestedValue={actions.setNestedValue}
						skipQuestion={actions.skipQuestion}
						text={text}
						errorMessage={errorMessage}
						elementIndex={elementIndex}
					/>
				)}
			{noInputBlocks.indexOf(block.typeName) === -1 && (
				<div
					id={`focus-dummy-${block.id}`}
					ref={conversationalDummyFocusBlockRef}
					className={makeClassName('conversational-focus-dummy')}
					tabIndex={lastKeyPressed ? 0 : -1}
				>
					{conversationalFocusStateValue}
				</div>
			)}
		</div>
	);
}
